import { attach, forward, guard, sample } from 'effector';
import { baseUrls$ } from 'model/config.model';
import { LocalizationOptions } from 'model/domain';
import { ErrorType, errorType$, setError } from 'model/error.model';
import { createSocket } from 'model/helpers/socket';
import { openLinkParams$ } from 'model/main.model';
import { RootDomain } from 'model/root.model';

const socket = createSocket();

export const connectCrossDeviceSocket = RootDomain.event<{ room: string }>();
export const onCrossDeviceParams = RootDomain.event<{
	token: string,
	applicationKey: string,
	localizationOptions: LocalizationOptions,
}>();
const onCrossDeviceConnected = RootDomain.event();
export const crossDeviceCancel = RootDomain.event<ErrorType>();
export const onCrossDeviceCanceled = RootDomain.event();
export const crossDeviceFinish = RootDomain.event();
export const onJoinedMobile = RootDomain.event<{ status: string }>();
export const onSessionDisconnect = RootDomain.event();

const room$ = RootDomain.store<string>(null);

const emitMobileEvent = attach({
	effect: socket.emit,
	source: { room: room$ },
	mapParams: (event: string | { event: string, body: any }, source) => {
		if (typeof event === 'object') {
			return {
				event: event.event,
				body: {
					...event.body,
					room: source.room,
				},
			};
		}

		return {
			event, body: { room: source.room },
		};
	},
});

room$
	.on(connectCrossDeviceSocket, (_, { room }) => room);

socket.on('connect', onCrossDeviceConnected);
socket.on('params[mobile]', onCrossDeviceParams);
socket.on('canceled[desktop]', onCrossDeviceCanceled);
socket.on('joined[mobile]', onJoinedMobile);
socket.on('session_disconnected', onSessionDisconnect);

sample({
	source: baseUrls$,
	clock: connectCrossDeviceSocket,
	fn: baseUrls => ({ baseUrl: baseUrls.apiBaseUrl }),
	target: socket.connect,
});
sample({
	source: { error: errorType$, params: openLinkParams$ },
	clock: onCrossDeviceConnected,
	fn: ({ error, params }) => ({ event: 'join[mobile]', body: { error, params } }),
	target: emitMobileEvent,
});
forward({
	from: crossDeviceFinish,
	to: emitMobileEvent.prepend(() => 'finish'),
});
forward({
	from: crossDeviceCancel,
	to: emitMobileEvent.prepend((errorType) => ({
		event: 'canceled[mobile]',
		body: { errorType },
	})),
});
forward({
	from: onSessionDisconnect,
	to: setError.prepend(() => ErrorType.invalid_session),
});
guard({
	source: onJoinedMobile,
	filter: (payload) => {
		const { status } = payload || {};
		return !(status === 'pending' || status === undefined);
	},
	target: setError.prepend(() => ErrorType.invalid_session),
});
